/**
 * Legacy zod-based identifier schemas.
 *
 * This module is the verbatim content of the former `@buildwithflux/identifiers`
 * package, relocated here as part of the identifiers domain migration (M1.3).
 *
 * It is exposed under the `@buildwithflux/identifiers-schema/legacy` subpath to
 * keep the root export reserved for the canonical Effect Schema shapes (see
 * `src/principals/`). New code should prefer the Effect Schema principals at
 * the root; this file remains for consumers that rely on the zod surface
 * (`.parse()` / `.safeParse()` calls, `z.union(...)`, etc.) and will be
 * progressively ported to Effect Schema in follow-up milestones.
 */
import z from "zod";

import {type BareUserUid, parseUserUidFromPrefixed as parseBareUserUidFromPrefixed} from "./principals/UserUid.js";

/**
 * Generate a UUID string for use as a GUID
 */
function guid(): string {
    return crypto.randomUUID();
}

// ---------------------------------------------------------------------------
// Document
// ---------------------------------------------------------------------------

/**
 * A document UID is a UUID string
 *
 * @todo Use a branded string
 */
export const DocumentUid = z.string().length(36);
export type DocumentUid = z.infer<typeof DocumentUid>;

// ---------------------------------------------------------------------------
// Enterprise
// ---------------------------------------------------------------------------

/**
 * An enterprise UID is a string that starts with "ent_" and ends with a UUID
 */
export type EnterpriseUid = `ent_${string}`;
export const EnterpriseUid = z
    .string()
    .startsWith("ent_")
    .refine((val: string): val is EnterpriseUid => !!val);

export function createNewEnterpriseUid(): EnterpriseUid {
    return EnterpriseUid.parse(`ent_${guid()}`);
}

export function isEnterpriseUid(identifier: string | undefined): identifier is EnterpriseUid {
    return EnterpriseUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Review Check
// ---------------------------------------------------------------------------

/**
 * A review check UID is a string that starts with "rch_"
 */
export type ReviewCheckUid = `rch_${string}`;
export const ReviewCheckUid = z
    .string()
    .startsWith("rch_")
    .refine((val: string): val is ReviewCheckUid => !!val);

export function createNewReviewCheckUid(name: string): ReviewCheckUid {
    return ReviewCheckUid.parse(`rch_${name.replace(/\s+/g, "_").toLowerCase()}`);
}

export function isReviewCheckUid(identifier: string | undefined): identifier is ReviewCheckUid {
    return ReviewCheckUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Review Column
// ---------------------------------------------------------------------------

/**
 * A review column UID is a string that starts with "rcl_"
 */
export type ReviewColumnUid = `rcl_${string}`;
export const ReviewColumnUid = z
    .string()
    .startsWith("rcl_")
    .refine((val: string): val is ReviewColumnUid => !!val);

export function createNewReviewColumnUid(name: string): ReviewColumnUid {
    return ReviewColumnUid.parse(`rcl_${name.replace(/\s+/g, "_").toLowerCase()}`);
}

export function isReviewColumnUid(identifier: string | undefined): identifier is ReviewColumnUid {
    return ReviewColumnUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Organization
// ---------------------------------------------------------------------------

/**
 * An organization UID is a string that starts with "org_" and ends with a UUID
 */
export type OrganizationUid = `org_${string}`;
export const OrganizationUid = z
    .string()
    .startsWith("org_")
    .refine((val: string): val is OrganizationUid => !!val);
export const optionalOrganizationUid = z.preprocess((v) => v ?? undefined, OrganizationUid.optional());

export function createNewOrganizationUid(): OrganizationUid {
    return `org_${guid()}`;
}

export function isOrganizationUid(identifier: string | undefined): identifier is OrganizationUid {
    return OrganizationUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// User / Account
// ---------------------------------------------------------------------------

/**
 * A legacy user UID is a bare alphanumeric string generated by Firebase auth (no prefix).
 *
 * @deprecated Prefer {@link PrefixedUserUid} (`usr_` format) for new code.
 *
 * This appears as a foreign key property on the main user interface IUserData, and is used to link the user to both:
 *  - The FirestoreAuth user, and
 *  - The user's private metadata bundle of properties (currently used for subscription information, private project counts, etc.)
 */
export const LegacyUserUid = z.string().min(2);
export type LegacyUserUid = string;

/**
 * Backward-compatible alias for {@link LegacyUserUid}.
 * @deprecated Use {@link LegacyUserUid} or {@link PrefixedUserUid} explicitly.
 */
export const UserUid = LegacyUserUid;
/** @deprecated Use {@link LegacyUserUid} or {@link PrefixedUserUid} explicitly. */
export type UserUid = LegacyUserUid;

/**
 * A prefixed user UID is a string that starts with "usr_" followed by the Firebase auth UID.
 *
 * New code should prefer this format for consistency with other prefixed identifiers (org_, ent_, etc.).
 * The template-literal type provides compile-time structural branding — a plain `string` or a
 * differently-prefixed identifier cannot be assigned to this type without an explicit assertion.
 *
 * Existing data may still use the legacy bare {@link LegacyUserUid} format.
 */
export type PrefixedUserUid = `usr_${string}`;
export const PrefixedUserUid = z
    .string()
    .startsWith("usr_")
    .refine((val: string): val is PrefixedUserUid => !!val);

/**
 * Type guard that checks whether a string is a prefixed user UID (starts with "usr_").
 */
export function isPrefixedUserUid(identifier: string | undefined): identifier is PrefixedUserUid {
    return PrefixedUserUid.safeParse(identifier).success;
}

/**
 * Prefixes of non-user principals. A bare Firebase auth UID is strictly
 * alphanumeric, so a value carrying one of these prefixes is an organization,
 * enterprise, or anonymous UID that must never be converted into a user UID.
 */
const NON_USER_PRINCIPAL_PREFIXES = ["org_", "ent_", "anon_"] as const;

/**
 * Converts a user UID to a {@link PrefixedUserUid}.
 * If the value already has the "usr_" prefix, it is returned as-is (idempotent).
 *
 * Accepts only user principals: a {@link LegacyUserUid} or a
 * {@link PrefixedUserUid}. Organization, enterprise, and anonymous UIDs are
 * rejected at runtime (in either bare or already-"usr_"-prefixed form), so a
 * non-user principal can never be laundered into the user-UID brand.
 */
export function toPrefixedUserUid(userUid: LegacyUserUid | PrefixedUserUid): PrefixedUserUid {
    const bare = parseUserUidFromPrefixed(userUid);
    for (const prefix of NON_USER_PRINCIPAL_PREFIXES) {
        if (bare.startsWith(prefix)) {
            throw new Error(`Refusing to convert non-user principal UID "${bare}" to a PrefixedUserUid`);
        }
    }
    if (isPrefixedUserUid(userUid)) {
        return userUid;
    }
    return `usr_${bare}`;
}

/**
 * Extracts the bare Firebase auth UID from a {@link PrefixedUserUid} by stripping the "usr_" prefix.
 * If the value does not have the prefix, it is returned as-is.
 *
 * Delegates to the canonical Effect Schema helper so the result carries the
 * {@link BareUserUid} brand instead of degrading to a plain string.
 */
export function parseUserUidFromPrefixed(identifier: string): BareUserUid {
    return parseBareUserUidFromPrefixed(identifier);
}

/**
 * A user identifier that accepts both legacy bare UIDs and new "usr_" prefixed UIDs.
 *
 * The Zod schema is a union that tries the narrower {@link PrefixedUserUid} first;
 * if the value does not start with "usr_", it falls back to {@link LegacyUserUid}
 * (`z.string().min(2)`).
 */
export const AnyUserUid = z.union([PrefixedUserUid, LegacyUserUid]);
export type AnyUserUid = LegacyUserUid | PrefixedUserUid;

/**
 * An account identifier: either a prefixed organization UID or a legacy bare user UID.
 *
 * The Zod schema tries the narrower {@link OrganizationUid} first, then falls back
 * to {@link LegacyUserUid}.
 */
export const AccountUid = z.union([OrganizationUid, LegacyUserUid]);
export type AccountUid = LegacyUserUid | OrganizationUid;

/**
 * An account-or-enterprise identifier: organization, enterprise, or legacy bare user UID.
 *
 * The Zod schema tries the narrower prefixed schemas first ({@link OrganizationUid},
 * {@link EnterpriseUid}), then falls back to {@link LegacyUserUid}.
 */
export const AccountOrEnterpriseUid = z.union([OrganizationUid, EnterpriseUid, LegacyUserUid]);
export type AccountOrEnterpriseUid = LegacyUserUid | OrganizationUid | EnterpriseUid;

// ---------------------------------------------------------------------------
// Anonymous UIDs
// ---------------------------------------------------------------------------

const ANON_PREFIX_RE = /^(?:anon_|any-anon_)/;
function stripAnonPrefix(uid: string): string {
    return uid.replace(ANON_PREFIX_RE, "");
}

/**
 * A specific anonymous session token — `anon_${string}`.
 */
export type AnonymousUid = `anon_${string}`;
export const AnonymousUid = z
    .string()
    .startsWith("anon_")
    .refine((val: string): val is AnonymousUid => !!val);

export function isAnonymousUid(identifier: string | undefined): identifier is AnonymousUid {
    return AnonymousUid.safeParse(identifier).success;
}

export function toAnonymousUid(uid: string): AnonymousUid {
    if (isAnonymousUid(uid)) {
        return uid;
    }
    return `anon_${stripAnonPrefix(uid)}`;
}

export function parseAnonymousUid(identifier: string): string {
    if (identifier.startsWith("anon_")) {
        return identifier.slice("anon_".length);
    }
    return identifier;
}

/**
 * Wildcard / nil anonymous identity — `any-anon_${string}`.
 */
export type AnyAnonymousUid = `any-anon_${string}`;
export const AnyAnonymousUid = z
    .string()
    .startsWith("any-anon_")
    .refine((val: string): val is AnyAnonymousUid => !!val);

export function isAnyAnonymousUid(identifier: string | undefined): identifier is AnyAnonymousUid {
    return AnyAnonymousUid.safeParse(identifier).success;
}

export function toAnyAnonymousUid(uid: string): AnyAnonymousUid {
    if (isAnyAnonymousUid(uid)) {
        return uid;
    }
    return `any-anon_${stripAnonPrefix(uid)}`;
}

export function parseAnyAnonymousUid(identifier: string): string {
    if (identifier.startsWith("any-anon_")) {
        return identifier.slice("any-anon_".length);
    }
    return identifier;
}

/**
 * The canonical nil-user identifier (prefixed form of the legacy `ANON_ROLE_UID`).
 */
export const NIL_ANONYMOUS_UID: AnyAnonymousUid = "any-anon_0338f920-f103-45be-9462-2cae879caa37";

export type SomeAnonymousUid = AnonymousUid | AnyAnonymousUid;
export const SomeAnonymousUid = z.union([AnonymousUid, AnyAnonymousUid]);

export function isSomeAnonymousUid(identifier: string | undefined): identifier is SomeAnonymousUid {
    return SomeAnonymousUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

/**
 * A prefixed project UID is a string that starts with "prj_" followed by an identifier
 * (typically the document UUID).
 *
 * New code should prefer this format for consistency with other prefixed identifiers (org_, ent_, usr_).
 * The template-literal type provides compile-time structural branding — a plain `string` or a
 * differently-prefixed identifier cannot be assigned to this type without an explicit assertion.
 *
 * Existing data may still use the legacy bare {@link DocumentUid} format for project identifiers.
 */
export type ProjectUid = `prj_${string}`;
export const ProjectUid = z
    .string()
    .startsWith("prj_")
    .refine((val: string): val is ProjectUid => !!val);

/**
 * Type guard that checks whether a string is a prefixed project UID (starts with "prj_").
 */
export function isProjectUid(identifier: string | undefined): identifier is ProjectUid {
    return ProjectUid.safeParse(identifier).success;
}

/**
 * Converts any project/document UID string to a {@link ProjectUid}.
 * If the value already has the "prj_" prefix, it is returned as-is (idempotent).
 */
export function toProjectUid(documentUid: string): ProjectUid {
    if (isProjectUid(documentUid)) {
        return documentUid;
    }
    return `prj_${documentUid}`;
}

/**
 * Extracts the bare document UID from a {@link ProjectUid} by stripping the "prj_" prefix.
 * If the value does not have the prefix, it is returned as-is.
 */
export function parseDocumentUidFromProjectUid(identifier: string): string {
    if (identifier.startsWith("prj_")) {
        return identifier.slice(4);
    }
    return identifier;
}

/**
 * A project identifier that accepts both legacy bare {@link DocumentUid}s and new "prj_" prefixed UIDs.
 *
 * The Zod schema tries the narrower {@link ProjectUid} first; if the value does not
 * start with "prj_", it falls back to {@link DocumentUid} (`z.string().length(36)`).
 */
export const AnyProjectUid = z.union([ProjectUid, DocumentUid]);
export type AnyProjectUid = DocumentUid | ProjectUid;

// ---------------------------------------------------------------------------
// Manufacturing Profile
// ---------------------------------------------------------------------------

/**
 * A manufacturing profile UID is a string that starts with "mfp_" followed by a UUID.
 *
 * Manufacturing profiles are reusable, publishable bundles of PCB rulesets that
 * encapsulate a board house's capabilities/constraints (e.g. "JLCPCB 2-layer standard").
 * They are stored in the `manufacturingProfiles` Firestore collection and published
 * as immutable versions under `manufacturingProfiles/{uid}/manufacturingProfileVersions/{version}`.
 */
export type ManufacturingProfileUid = `mfp_${string}`;
export const ManufacturingProfileUid = z
    .string()
    .startsWith("mfp_")
    .refine((val: string): val is ManufacturingProfileUid => !!val);

export function createNewManufacturingProfileUid(): ManufacturingProfileUid {
    return `mfp_${guid()}`;
}

export function isManufacturingProfileUid(identifier: string | undefined): identifier is ManufacturingProfileUid {
    return ManufacturingProfileUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Fab House
// ---------------------------------------------------------------------------

/**
 * A fab house UID is a string that starts with "fh_" followed by a UUID.
 *
 * Fab houses are board manufacturers (e.g. JLCPCB, PCBWay, OSH Park). They are
 * stored in the `fabHouses` Firestore collection.
 */
export type FabHouseUid = `fh_${string}`;
export const FabHouseUid = z
    .string()
    .startsWith("fh_")
    .refine((val: string): val is FabHouseUid => !!val);

export function createNewFabHouseUid(): FabHouseUid {
    return `fh_${guid()}`;
}

export function isFabHouseUid(identifier: string | undefined): identifier is FabHouseUid {
    return FabHouseUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Fabrication Order
// ---------------------------------------------------------------------------

export type FabricationOrderUid = `fo_${string}`;
export const FabricationOrderUid = z
    .string()
    .startsWith("fo_")
    .refine((val: string): val is FabricationOrderUid => !!val);

export function createNewFabricationOrderUid(): FabricationOrderUid {
    return `fo_${guid()}`;
}

export function isFabricationOrderUid(identifier: string | undefined): identifier is FabricationOrderUid {
    return FabricationOrderUid.safeParse(identifier).success;
}

// ---------------------------------------------------------------------------
// Node / Element / Vertex
// ---------------------------------------------------------------------------

// TODO we should start using branded types for this
export type NodeUid = string;

export type ElementUid = string & {__brand: "ElementUid"};

export type VertexUid = string & {__brand: "VertexUid"};

export function brandVertexUid(uid: string): VertexUid {
    // No checking here. This is simply to help us avoid mixing uid types by opting into the
    // branded type as early as possible.
    return uid as VertexUid;
}

// ---------------------------------------------------------------------------
// Auto Layout
// ---------------------------------------------------------------------------

export type AutoLayoutJobUid = `auto-layout-${string}`;
export type AutoLayoutJobMetadataUid = `auto-layout-${string}-metadata`;
export type AutoLayoutStepHash = `auto-layout-step-sha256-${string}`;

export const autoLayoutJobUidSchema = z.custom<AutoLayoutJobUid>(
    (s) => typeof s === "string" && s.startsWith("auto-layout-"),
);
export const autoLayoutJobMetadataUidSchema = z.custom<AutoLayoutJobMetadataUid>(
    (s) => typeof s === "string" && s.startsWith("auto-layout-") && s.endsWith("-metadata"),
);

export const autoLayoutStepHashSchema = z.custom<AutoLayoutStepHash>(
    (s) => typeof s === "string" && s.startsWith("auto-layout-step-sha256-"),
);
